import { Injectable, LOCALE_ID, inject, signal } from '@angular/core';
import { formatDate } from '@angular/common';

import { ApiService } from '../../shared/services/api.service';
import { InstitutionType } from './institution.service';
import { DateRange } from './state.service';
import { map, Observable, of, shareReplay } from 'rxjs';

export interface DateSeries {
  dates: Date[];
  labels: string[];
}

interface ReportDates {
  institutionType: InstitutionType;
  dates: Date[];
}

@Injectable({
  providedIn: 'root'
})
export class DateService {
  private apiService = inject(ApiService);
  private locale = inject(LOCALE_ID);
  private dateMap = new Map<InstitutionType, Date[]>();
  private nmdDateMap = new Map<InstitutionType, Date[]>();

  readonly datesFetched = signal(false);

  constructor() {
    this.apiService.get<ReportDates[]>('/api/date').subscribe(dates => {
      dates.forEach(d => {
        this.dateMap.set(d.institutionType, d.dates.map(d => new Date(d)));
      });
      this.datesFetched.set(true);
    });

    this.apiService.get<ReportDates[]>('/api/date/nmd').subscribe(dates => {
      dates.forEach(d => {
        this.nmdDateMap.set(d.institutionType, d.dates.map(d => new Date(d)));
      });
      this.datesFetched.set(true);
    });
  }

  private futureDatesMap = new Map<InstitutionType, Observable<{ labels: Date[] }>>();  // Adjusted type to store Date objects

  public getFutureDates(institutionType: InstitutionType): Observable<{ labels: Date[] }> {
    if (!this.futureDatesMap.has(institutionType)) {
      const futureDates$ = this.apiService.get<ReportDates[]>('/api/date/future').pipe(
        map(dates => {
          const institutionDates = dates.find(d => d.institutionType === institutionType)?.dates || [];
          // Directly return the Date objects without formatting
          return {
            labels: institutionDates.map(date => new Date(date))  // Convert string dates to Date objects
          };
        }),
        shareReplay(1) // Cache the result
      );

      this.futureDatesMap.set(institutionType, futureDates$);
    }

    return this.futureDatesMap.get(institutionType) ?? of({ labels: [] });
  }



  getCurrentDate(institutionType: InstitutionType): Date {
    const dates = this.getDates(institutionType);
    return dates[dates.length - 1];
  }

  getDates(institutionType: InstitutionType): Date[] {
    return this.dateMap.get(institutionType) ?? [];
  }

  getNmdDates(institutionType: InstitutionType): Date[] {
    return this.nmdDateMap.get(institutionType) ?? [];
  }

  getDateSeries(institutionType: InstitutionType, dateRange: DateRange, yearEnd: boolean = false, currentProduct?: string): DateSeries {
    let dates: Date[];
    if (yearEnd) {
      dates = this.getYearEndDates(institutionType, dateRange).filter(d => d >= dateRange.startDate && d <= dateRange.endDate);
    }
    else {
      if (currentProduct == 'non-maturity-deposits') {
        dates = this.getNmdDates(institutionType).filter(d => {
          // Convert all dates to date-only strings (YYYY-MM-DD) for comparison
          const dateOnly = new Date(d).toISOString().split('T')[0];
          const startDateOnly = new Date(dateRange.startDate).toISOString().split('T')[0];
          const endDateOnly = new Date(dateRange.endDate).toISOString().split('T')[0];
          return dateOnly >= startDateOnly && dateOnly <= endDateOnly;
        });
      } else {
        dates = this.getDates(institutionType).filter(d => d >= dateRange.startDate && d <= dateRange.endDate);
      }
    }
    return {
      dates: dates,
      labels: dates.map(d => formatDate(d, 'MMM yyyy', this.locale))
    };
  }

  getYearEndDates(institutionType: InstitutionType, dateRange: DateRange): Date[] {
    return this.getDates(institutionType).filter(d => d.getMonth() == 11 || d.getFullYear() == dateRange.endDate.getFullYear());
  }
}
